import React from 'react';
import styled from 'styled-components';
import { useLocation, Link } from 'react-router-dom';
import { useApi } from '../../../hooks/useApi';
import { PageData, TextLink, Breadcrumb } from '../../../types/api';
import { LOADING_MESSAGES, ERROR_MESSAGES } from '../../../utils/constants';

const Container = styled.div`
  max-width: ${({ theme }) => theme.breakpoints.wide};
  margin: 0 auto;
  padding: ${({ theme }) => theme.spacing.xl} ${({ theme }) => theme.spacing.sm};
`;

const Breadcrumbs = styled.nav`
  margin-bottom: ${({ theme }) => theme.spacing.lg};
  font-size: 0.9rem;
`;

const BreadcrumbList = styled.ol`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing.xs};
`;

const BreadcrumbItem = styled.li`
  display: flex;
  align-items: center;
  
  &:not(:last-child)::after {
    content: '›';
    margin-left: ${({ theme }) => theme.spacing.xs};
    color: ${({ theme }) => theme.colors.textLight};
  }
`;

const BreadcrumbLink = styled(Link)`
  color: ${({ theme }) => theme.colors.accent};
  text-decoration: none;
  
  &:hover {
    text-decoration: underline;
  }
  
  &:focus {
    outline: 2px solid ${({ theme }) => theme.colors.accent};
    outline-offset: 2px;
  }
`;

const BreadcrumbCurrent = styled.span`
  color: ${({ theme }) => theme.colors.textLight};
`;

const PageTitle = styled.h1`
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`;

const ContentBody = styled.div`
  font-size: 1rem;
  line-height: 1.6;
  margin-bottom: ${({ theme }) => theme.spacing.xl};
  
  p {
    margin-bottom: ${({ theme }) => theme.spacing.md};
  }
  
  h2, h3, h4, h5, h6 {
    margin-top: ${({ theme }) => theme.spacing.lg};
    margin-bottom: ${({ theme }) => theme.spacing.sm};
  }
  
  ul, ol {
    margin-bottom: ${({ theme }) => theme.spacing.md};
    padding-left: ${({ theme }) => theme.spacing.lg};
  }
  
  li {
    margin-bottom: ${({ theme }) => theme.spacing.xs};
  }
  
  a {
    color: ${({ theme }) => theme.colors.accent};
    
    &:hover {
      text-decoration: underline;
    }
  }
  
  blockquote {
    margin: ${({ theme }) => theme.spacing.lg} 0;
    padding: ${({ theme }) => theme.spacing.md};
    border-left: 4px solid ${({ theme }) => theme.colors.accent};
    background-color: #f9f9f9;
    font-style: italic;
  }
  
  img {
    max-width: 100%;
    height: auto;
    margin: ${({ theme }) => theme.spacing.md} 0;
  }
`;

const RelatedLinksSection = styled.aside`
  margin-top: ${({ theme }) => theme.spacing.xl};
  padding-top: ${({ theme }) => theme.spacing.lg};
  border-top: 1px solid ${({ theme }) => theme.colors.border};
`;

const RelatedLinksTitle = styled.h2`
  font-size: 1.25rem;
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const RelatedLinksList = styled.ul`
  list-style: none;
  padding: 0;
  
  li {
    margin-bottom: ${({ theme }) => theme.spacing.sm};
  }
`;

const RelatedLink = styled(Link)`
  color: ${({ theme }) => theme.colors.accent};
  text-decoration: none;
  font-size: 1.1rem;
  
  &:hover {
    text-decoration: underline;
  }
  
  &:focus {
    outline: 2px solid ${({ theme }) => theme.colors.accent};
    outline-offset: 2px;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${({ theme }) => theme.spacing.xxl};
  color: ${({ theme }) => theme.colors.textLight};
`;

const ErrorContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${({ theme }) => theme.spacing.xxl};
  color: #d32f2f;
  background-color: #ffebee;
  border: 1px solid #ffcdd2;
  border-radius: 4px;
  margin: ${({ theme }) => theme.spacing.md} 0;
`;

export const ContentPage: React.FC = () => {
  const location = useLocation();
  
  // Extract slug from pathname (remove leading slash)
  const slug = location.pathname.substring(1);
  const { data: pageData, loading, error } = useApi<PageData>(`/pages/content/${slug}.json`);

  if (loading) {
    return (
      <Container>
        <LoadingContainer>{LOADING_MESSAGES.CONTENT}</LoadingContainer>
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <ErrorContainer>
          {ERROR_MESSAGES.LOADING_ERROR}: {error}
        </ErrorContainer>
      </Container>
    );
  }

  if (!pageData) {
    return (
      <Container>
        <ErrorContainer>{ERROR_MESSAGES.PAGE_NOT_FOUND}</ErrorContainer>
      </Container>
    );
  }

  const content = pageData.content as any; // Type assertion for content page
  const navigation = pageData.navigation;

  return (
    <Container>
      {navigation?.breadcrumbs && navigation.breadcrumbs.length > 0 && (
        <Breadcrumbs aria-label="Breadcrumb navigation">
          <BreadcrumbList>
            {navigation.breadcrumbs.map((crumb: Breadcrumb, index: number) => (
              <BreadcrumbItem key={`breadcrumb-${index}-${crumb.title.replace(/\s+/g, '-').toLowerCase()}`}>
                {index === navigation.breadcrumbs!.length - 1 ? (
                  <BreadcrumbCurrent aria-current="page">
                    {crumb.title}
                  </BreadcrumbCurrent>
                ) : (
                  <BreadcrumbLink to={crumb.link}>
                    {crumb.title}
                  </BreadcrumbLink>
                )}
              </BreadcrumbItem>
            ))}
          </BreadcrumbList>
        </Breadcrumbs>
      )}

      <PageTitle>{pageData.title}</PageTitle>

      {content.body && (
        <ContentBody 
          dangerouslySetInnerHTML={{ __html: content.body }} 
        />
      )}

      {content.relatedLinks && content.relatedLinks.length > 0 && (
        <RelatedLinksSection>
          <RelatedLinksTitle>Related Information</RelatedLinksTitle>
          <RelatedLinksList>
            {(content.relatedLinks as TextLink[]).map((link, index) => (
              <li key={`related-${index}-${link.title.replace(/\s+/g, '-').toLowerCase()}`}>
                <RelatedLink to={link.link}>
                  {link.title}
                </RelatedLink>
              </li>
            ))}
          </RelatedLinksList>
        </RelatedLinksSection>
      )}
    </Container>
  );
};

export default ContentPage;
